import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';

/**
 * ResetPasswordPage · `/auth/reset-password`
 *
 * Pantalla pública (no requiere sesión). El usuario ingresa su email
 * y disparamos `resetPasswordForEmail` con redirect a `/auth/callback?type=recovery`,
 * que después manda a `/auth/update-password` para fijar la nueva clave.
 *
 * No revelamos si el email existe o no (anti-enumeration): siempre
 * mostramos el mismo mensaje "te mandamos un email si la cuenta existe".
 */
const schema = z.object({
  email: z.string().email('Email inválido'),
});
type Values = z.infer<typeof schema>;

const REDIRECT_URL = `${window.location.origin}/auth/callback?type=recovery`;

export function ResetPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: Values) {
    const { error } = await supabase.auth.resetPasswordForEmail(
      values.email.trim().toLowerCase(),
      { redirectTo: REDIRECT_URL },
    );
    // No revelamos error específico — anti-enumeration
    if (error && error.status !== 400) {
      toast.error('Hubo un problema técnico. Intentá de nuevo en un minuto.');
      return;
    }
    setSubmittedEmail(values.email.trim().toLowerCase());
    setSubmitted(true);
  }

  return (
    <main className="bg-background flex min-h-screen items-center justify-center p-6">
      <Card className="border-border bg-card w-full max-w-sm">
        <CardHeader className="space-y-1.5">
          <CardTitle className="text-title">DenDev Core</CardTitle>
          <p className="text-fg-muted text-body">
            Recuperar contraseña
          </p>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <MailCheck className="text-primary h-7 w-7" />
                <p className="text-foreground text-body-lg font-semibold">
                  Revisá tu casilla
                </p>
                <p className="text-fg-muted text-caption">
                  Si <code className="font-mono">{submittedEmail}</code> existe,
                  te mandamos un link para definir una contraseña nueva. El link
                  expira en 1 hora.
                </p>
              </div>
              <Button asChild variant="ghost" className="w-full">
                <Link to="/login">
                  <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                  Volver al login
                </Link>
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email de la cuenta</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          autoFocus
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="text-caption">
                        Te mandamos un magic link para definir nueva contraseña.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting
                    ? 'Enviando…'
                    : 'Mandar link de recuperación'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  asChild
                >
                  <Link to="/login">
                    <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
                    Volver al login
                  </Link>
                </Button>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
